import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { RecurlyProvider } from '@recurly/react-recurly';
import useTenancy from '../../hooks/useTenancy';
import { useTranslation } from 'react-i18next';

const RECURLY_PUBLIC_KEY = process.env.REACT_APP_RECURLY_PUBLIC_KEY || '';
const TEST_TENANCY_RECURLY_PUBLIC_KEY = process.env.REACT_APP_TEST_TENANCY_RECURLY_PUBLIC_KEY || '';

// Maximum time to wait for recurly.js to load (in ms)
const RECURLY_LOAD_TIMEOUT_MS = 10000;
// Polling interval to check if recurly.js is available (in ms)
const RECURLY_POLL_INTERVAL_MS = 50;

interface FiRecurlyProviderProps {
  children: JSX.Element;
  /**
   * If true, the fact that recurly.js is not available is not 100% necessary for the user to continue
   * For example: recurly.js is necessary for the Apple Pay and PayPal button, but the user can continue with a
   * credit card number.
   */
  nonEssential?: boolean;
}

/**
 * Custom hook to wait for recurly.js to be available on window.
 * Since recurly.js is loaded with `defer`, it may not be immediately available when React renders.
 * This hook polls for window.recurly and returns the loading state.
 */
function useRecurlyReady(): { isReady: boolean; timedOut: boolean } {
  const [isReady, setIsReady] = useState(!!window.recurly);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    // If recurly is already available, no need to poll
    if (window.recurly) {
      setIsReady(true);
      return;
    }

    const startTime = Date.now();

    const checkInterval = setInterval(() => {
      if (window.recurly) {
        setIsReady(true);
        clearInterval(checkInterval);
      } else if (Date.now() - startTime >= RECURLY_LOAD_TIMEOUT_MS) {
        // Timeout reached, stop polling
        setTimedOut(true);
        clearInterval(checkInterval);
      }
    }, RECURLY_POLL_INTERVAL_MS);

    return () => clearInterval(checkInterval);
  }, []);

  return { isReady, timedOut };
}

/**
 * Recurly provider with the Fi public key. Best use of this is to wrap only the elements that need to use Recurly.
 *
 * This component waits for recurly.js to load (since it's loaded with `defer`) before rendering
 * the RecurlyProvider. This prevents a race condition where React renders before the deferred
 * script has executed.
 */
export default function FiRecurlyProvider({ children, nonEssential }: FiRecurlyProviderProps) {
  const tenancy = useTenancy();
  const { t } = useTranslation();
  const { isReady, timedOut } = useRecurlyReady();

  // Still waiting for recurly.js to load
  if (!isReady && !timedOut) {
    // For non-essential components (Apple Pay, PayPal), don't show anything while loading
    // For essential components (credit card form), show nothing briefly while we wait
    // This prevents the jarring error message flash on slow connections
    return null;
  }

  // Recurly provider will throw 'Please load Recurly.js (https://js.recurly.com/v4/recurly.js) on this page prior to
  // loading your React application.' if window.recurly is not defined. The most common reason we've seen is because
  // it's a Google crawler and Recurly's domain has a robots.txt
  if (isReady && window.recurly) {
    if (tenancy === 'test') {
      console.debug(`Using test tenancy Recurly public key: ${TEST_TENANCY_RECURLY_PUBLIC_KEY}`);
      return <RecurlyProvider publicKey={TEST_TENANCY_RECURLY_PUBLIC_KEY}>{children}</RecurlyProvider>;
    }
    console.debug(`Using prod tenancy Recurly public key: ${RECURLY_PUBLIC_KEY}`);
    return <RecurlyProvider publicKey={RECURLY_PUBLIC_KEY}>{children}</RecurlyProvider>;
  }

  // Timed out or recurly blocked (e.g., Google crawler with robots.txt blocking)
  Sentry.captureMessage('Recurly.js failed to load', {
    level: 'warning',
    tags: {
      component: 'FiRecurlyProvider',
      timedOut: timedOut.toString(),
      nonEssential: (nonEssential ?? false).toString(),
    },
  });

  if (nonEssential) {
    return null;
  }

  return <h3>{t('unable_to_load_billing_form_please_reload_or_contact_support')}</h3>;
}
